import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import logger from '../../logger';
/**
 * 人数を見に行く間隔(ms)。
 *
 * 待機画面に居る間に他の人が出入りするので、一度読んだきりにしない。
 * 会議サーバー側は memory 上の数を返すだけなので、この程度なら負荷にならない。
 */
const REFRESH_MS = 15000;
const useStyles = makeStyles()(() => {
    return {
        /*
         * 見た目は deepai-theme の CSS 変数に合わせる（html[data-theme] に載っている）。
         * Jitsi 標準のパレットを使うと、生成りの natural テーマの上で読めなくなる。
         */
        box: {
            background: 'var(--surface-2, rgba(28,38,72,.42))',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: 'var(--radius-sm, 14px)',
            color: 'var(--text, #eaf0ff)',
            font: '400 13px/1.6 var(--font, system-ui, sans-serif)',
            marginBottom: 16,
            padding: '10px 14px',
            textAlign: 'center',
            width: '100%'
        },
        // 満員のときだけ色を変える。押しても入れないことを先に伝える。
        boxFull: {
            background: 'var(--surface, rgba(20,28,54,.55))',
            borderColor: 'var(--danger, #D83848)'
        },
        label: {
            color: 'var(--text-dim, #93a0c4)',
            letterSpacing: '.06em',
            marginRight: 8
        },
        count: {
            fontSize: 15,
            fontWeight: 700
        },
        countFull: {
            color: 'var(--danger, #D83848)'
        },
        note: {
            color: 'var(--text-dim, #93a0c4)',
            fontSize: 12,
            marginTop: 4
        },
        // 利用中（定員なしの部屋で、誰かが既に入っているとき）。
        boxInUse: {
            borderColor: 'var(--accent, #4C7DF0)'
        },
        inUse: {
            color: 'var(--accent, #4C7DF0)',
            fontSize: 15,
            fontWeight: 700
        }
    };
});
/**
 * 部屋の定員と、いま何人入っているかを待機画面に出す。
 *
 * 取得先は会議サーバー自身（/room-status）。会議ページと同一オリジンなので
 * CORS は要らない。
 *
 * ★人数の数え方をここに書かない。会議サーバーが「弾く判定に使っているのと同じ
 *   関数」で数えた結果をそのまま出す。ここで数え直すと、表示と実際の可否が
 *   食い違う（AI と司会役 focus を数えない、という規則がずれる）。
 *
 * ★上限が無い部屋では何も出さない。今までの待機画面と同じ見た目に保つ。
 *
 * ★これは保証ではない。読んだ後・参加を押すまでの間に他の人が入れば弾かれる。
 *   そのときは従来どおり「参加者数の上限に達しました」が出るので破綻はしない。
 *
 * @returns {JSX.Element|null}
 */
const RoomCapacity = () => {
    const { classes, cx } = useStyles();
    const room = useSelector((state) => state['features/base/conference'].room ?? '');
    const [cap, setCap] = useState(null);
    useEffect(() => {
        if (!room) {
            return;
        }
        const ac = new AbortController();
        let timer;
        const read = async () => {
            try {
                const res = await fetch(`/room-status?room=${encodeURIComponent(room)}`, { signal: ac.signal });
                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }
                const j = await res.json();
                setCap({
                    full: j?.full === true,
                    max_occupants: typeof j?.max_occupants === 'number' ? j.max_occupants : null,
                    occupants: typeof j?.occupants === 'number' ? j.occupants : 0
                });
            }
            catch (e) {
                if (e?.name === 'AbortError') {
                    return;
                }
                // 取れないことは異常ではない（上限機能を入れていないサーバーもある）。
                // 黙って出さないでおく。入室そのものは止めない。
                logger.warn('RoomCapacity: 定員を取得できませんでした', e);
                setCap(null);
            }
            timer = setTimeout(read, REFRESH_MS);
        };
        read();
        return () => {
            ac.abort();
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [room]);
    if (!cap) {
        return null;
    }
    // 上限が無い部屋。誰も居なければ今までどおり何も出さないが、
    // 既に誰か入っていれば「利用中」だけ知らせる。
    if (cap.max_occupants === null) {
        if (cap.occupants < 1) {
            return null;
        }
        return (React.createElement("div", { className: cx(classes.box, classes.boxInUse) },
            React.createElement("span", { className: classes.inUse }, '利用中'),
            React.createElement("div", { className: classes.note }, `現在 ${cap.occupants} 人が参加しています。`)));
    }
    return (React.createElement("div", { className: cx(classes.box, cap.full && classes.boxFull) },
        React.createElement("span", { className: classes.label }, cap.full ? '満員です' : '定員'),
        React.createElement("span", { className: cx(classes.count, cap.full && classes.countFull) },
            cap.occupants,
            " / ",
            cap.max_occupants,
            " \u4EBA"),
        cap.full && (React.createElement("div", { className: classes.note }, '誰かが退出するまで入室できません。'))));
};
export default RoomCapacity;
